import type { ActionDefinition } from '@gzl10/nexus-sdk'
import { useSelectField } from '@gzl10/nexus-sdk/fields'
import { PREDEFINED_MASTERS, DEFAULT_MASTER_TYPES } from '../constants.js'

/**
 * Install a predefined master type — loads data from the framework catalog.
 * Only available in development (handler throws ForbiddenError in production).
 */
export const installTypeAction: ActionDefinition = {
  key: 'install-type',
  scope: 'module',
  label: { en: 'Install Master Type', es: 'Instalar Tipo de Maestro' },
  icon: 'mdi:database-plus',
  variant: 'primary',
  output: {},
  input: {
    type: useSelectField({
      label: { en: 'Master Type', es: 'Tipo de Maestro' },
      required: true,
      options: Object.keys(PREDEFINED_MASTERS)
        .filter(t => !DEFAULT_MASTER_TYPES.includes(t as typeof DEFAULT_MASTER_TYPES[number]))
        .map(t => ({
          value: t,
          label: t.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        })),
    }),
  },

  handler: async (ctx, input) => {
    if (process.env['NODE_ENV'] === 'production') {
      throw new ctx.core.errors.ForbiddenError('Master type management is only available in development')
    }

    const { type } = input as { type: string }
    const loader = PREDEFINED_MASTERS[type]
    if (!loader) {
      throw new ctx.core.errors.AppError(`Unknown predefined master type: ${type}`, 400)
    }

    // Check if already installed
    const existing = await ctx.db.knex('masters').where({ type }).first()
    if (existing) {
      throw new ctx.core.errors.ConflictError(`Master type "${type}" is already installed`)
    }

    const entries = await loader()
    const rows = entries.map((entry, i) => ({
      id: `${type}:${entry.code}`,
      type,
      code: entry.code,
      label: JSON.stringify(typeof entry.label === 'string' ? { en: entry.label } : entry.label),
      order: entry.order ?? i,
      is_active: entry.is_active ?? true,
      metadata: entry.metadata ? JSON.stringify(entry.metadata) : null,
    }))

    await ctx.db.knex('masters')
      .insert(rows)
      .onConflict(['type', 'code'])
      .ignore()

    return { installed: type, count: rows.length }
  },
}
